import { Pipe, PipeTransform, inject } from '@angular/core';
import {
  DvButtonElementParams,
  DvDatePeriodPickerElementParams,
  DvDatePeriodPickerLanguage,
  DvDatePickerLegacyElementParams,
  DvDateTimePickerElementParams,
  DvDateTimePickerLanguage,
  DvDropdownClientSideParams,
  DvSlideToggleParams,
  DvTimePickerParams,
  DvDynamicFormElement,
  DvElementType,
  DvBaseEditorParams,
} from '@devinco/ui/legacy';
import { TranslateService } from '@ngx-translate/core';
import { TranslationLanguage } from '@shared/constants/translation-lang';

@Pipe({
  name: 'translateElements',
  standalone: false,
})
export class DynamicFormTranslatePipe implements PipeTransform {
  private translate = inject(TranslateService);

  transform(elements?: DvDynamicFormElement[]): DvDynamicFormElement[] {
    elements?.forEach(({ options }) => {
      if (options.infoIconText) {
        options.infoIconText = this.translate.instant(options.infoIconText);
      }

      switch (options.elementType) {
        case DvElementType.Checkbox:
        case DvElementType.CheckboxLegacy: {
          const checkbox = options as any;
          if (checkbox.checkboxLabel) {
            checkbox.checkboxLabel = this.translate.instant(
              checkbox.checkboxLabel
            );
          }
          break;
        }
        case DvElementType.Dropdown: {
          const dropdown = options as DvDropdownClientSideParams;
          dropdown.clearAllText = this.translate.instant(
            dropdown.clearAllText || 'General.ClearAll'
          );
          if (dropdown.footerText) {
            dropdown.footerText = this.translate.instant(dropdown.footerText);
          }

          if (dropdown.loadingText) {
            dropdown.loadingText = this.translate.instant(dropdown.loadingText);
          }

          if (dropdown.isMultipleSelectionEnabled) {
            dropdown.counterLabelText = this.translate.instant(
              'General.SelectedItemsCount'
            );
          }

          if (dropdown.isSelectAllEnabled) {
            dropdown.selectAllText =
              this.translate.instant('General.SelectAll');
          }

          dropdown.noItemsFoundText = this.translate.instant(
            'General.NoItemsFound'
          );
          break;
        }
        case DvElementType.SlideToggle:
        case DvElementType.Toggle: {
          const slideToggle = options as DvSlideToggleParams;
          if (slideToggle.slideToggleLabel) {
            slideToggle.slideToggleLabel = this.translate.instant(
              slideToggle.slideToggleLabel
            );
          }
          break;
        }
        case DvElementType.Button: {
          const button = options as DvButtonElementParams;
          if (button.text) {
            button.text = this.translate.instant(button.text).toUpperCase();
          }
          break;
        }
        case DvElementType.TimePicker: {
          const timePicker = options as DvTimePickerParams;
          timePicker.hoursTitle = this.translate
            .instant('General.Hours')
            .toUpperCase();
          timePicker.minutesTitle = this.translate
            .instant('General.Minutes')
            .toUpperCase();
          break;
        }
        case DvElementType.DatePeriodPicker: {
          const datePeriodPicker = options as DvDatePeriodPickerElementParams;
          datePeriodPicker.daysTranslation =
            this.translate.instant('General.Days');
          datePeriodPicker.intervalTranslation =
            this.translate.instant('General.Interval');
          datePeriodPicker.allTranslation =
            this.translate.instant('General.All');
          datePeriodPicker.confirmBtnText =
            this.translate.instant('General.Apply');
          datePeriodPicker.cancelBtnText =
            this.translate.instant('General.Cancel');
          datePeriodPicker.language =
            this.getPickerLanguage() as DvDatePeriodPickerLanguage;

          datePeriodPicker.filters = datePeriodPicker.filters?.map(period => ({
            ...period,
            title: this.translate.instant(period.filterKey || period.title),
          }));
          break;
        }
        case DvElementType.DateTimePicker: {
          const dateTimePicker = options as DvDateTimePickerElementParams;
          dateTimePicker.hoursTitle = this.translate
            .instant('General.Hours')
            .toUpperCase();
          dateTimePicker.minutesTitle = this.translate
            .instant('General.Minutes')
            .toUpperCase();
          dateTimePicker.language =
            this.getPickerLanguage() as DvDateTimePickerLanguage;
          break;
        }
        case DvElementType.DatePicker:
        case DvElementType.DatePickerLegacy: {
          const datePicker = options as DvDatePickerLegacyElementParams;
          datePicker.language =
            this.getPickerLanguage() as DvDateTimePickerLanguage;
          break;
        }
      }

      // Common
      if (options.label) {
        options.label = this.translate.instant(options.label);
      }

      if (options.hintLeft) {
        options.hintLeft = this.translate.instant(options.hintLeft);
      }

      if (options.hintRight) {
        options.hintRight = this.translate.instant(options.hintRight);
      }

      const common = options as { tooltip?: string; placeholder?: string };
      if (common.tooltip) {
        common.tooltip = this.translate.instant(common.tooltip);
      }

      if (common.placeholder) {
        common.placeholder = this.translate.instant(common.placeholder);
      }

      const baseOptions = options as DvBaseEditorParams;
      baseOptions.validationMessages = this.provideErrorMessages(
        baseOptions.validationMessages
      );
    });
    return elements || [];
  }

  private provideErrorMessages(
    validationMessages: { [key: string]: string } | undefined
  ): { [key: string]: string } {
    validationMessages = validationMessages ? validationMessages : {};
    validationMessages['required'] = this.translate.instant(
      'Validation.Required'
    );
    validationMessages['lowerThanOtherControlValidaror'] =
      this.translate.instant('Validation.ValueTooHigh');
    validationMessages['min'] = this.translate.instant('Validation.Min');
    validationMessages['max'] = this.translate.instant('Validation.Max');
    validationMessages['maxlength'] = this.translate.instant(
      'Validation.MaxLength'
    );
    validationMessages['phoneValidation'] = this.translate.instant(
      'Validation.PhoneInvalid'
    );
    validationMessages['phoneErrorInvalidLength'] = this.translate.instant(
      'Validation.PhoneErrorInvalidLength'
    );
    validationMessages['phoneErrorTooShort'] = this.translate.instant(
      'Validation.PhoneErrorTooShort'
    );
    validationMessages['phoneErrorNotAllowed'] = this.translate.instant(
      'Validation.PhoneErrorNotAllowed'
    );
    validationMessages['phoneErrorNotPhoneNumber'] = this.translate.instant(
      'Validation.PhoneErrorNotPhoneNumber'
    );
    validationMessages['phoneErrorTooLong'] = this.translate.instant(
      'Validation.PhoneErrorTooLong'
    );
    validationMessages['lowerThanOtherControlValidator'] =
      this.translate.instant('Validation.LowerThanOtherControlValidator');

    validationMessages['whiteSpace'] = this.translate.instant(
      'Validation.WhiteSpace'
    );
    validationMessages['timeRangeAndNotEqualError'] = this.translate.instant(
      'Validation.RangeEndAndNotEqualError'
    );
    validationMessages['mask'] = this.translate.instant(
      'Validation.FormatIsWrong'
    );
    return validationMessages;
  }

  private getPickerLanguage(): string {
    return (
      this.translate.currentLang === TranslationLanguage.Development
        ? TranslationLanguage.EN
        : this.translate.currentLang
    ) as DvDateTimePickerLanguage;
  }
}
